import useTitanApp from "@/hooks/useTitanApp";
import { useState } from "react";
import {
  getPatientForNHSEmergencyService,
  getReferralDataByConsultationId,
  setMatchDialogModal,
  setMatchPatient,
  setShowNHS111ReferralTab,
  updateConsultationForPatientMatch,
  updateConsultationStage,
} from "@/dispatchers/pharmacyServiceDispatchers";
import {
  openPatientForm,
  getPatientConsultationByConsultationId,
  getPatientByPatientId,
  setConsultationId,
  setFreeTypeMode,
  setIsFinalSummary,
  setPatientNotes,
  setSignatureDetails,
  setTranscript,
  setConsultationPaused,
  setCurrentConsultationMediaButtonMode,
  setIsConsultationEnabled,
  setPatientTranscription,
  setSelectedPatientConsultation,
  setShowPlayPauseBtn,
  setAiChatSuccessMessages,
  setAddPatientAIChatMessage,
  setPreConsultationTemplate,
  setPreConsultationTranscription,
  setisHandleStartConsultation,
  setNhs111Form,
  setNhs111Prescriptions,
  setNhs111EmergencySupplyMedications,
  setNhsContraceptionForm,
  setNhsSinusitisForm,
  setNhsUtiForm,
  setNhsSoreThroatForm,
  setNhsImpetigoForm,
  setNhsInsectBitesForm,
  setNhsShinglesForm,
  setNhsAomForm,
  setHypertensionInitialForm,
  setHypertensionABPMFittingForm,
  setHypertensionABPMFollowupForm,
  setNhsAhtForm,
  setNewPatientFormDefaults,
} from "@/dispatchers/patientDispatchers";
import {
  setPatientInfoTab,
  setisPatientSearched,
  setCurrentGlobalTabValue,
  setCurrentGlobalTabText,
} from "@/dispatchers/appDispatchers";
import { useNavigate } from "react-router";
import {
  setSelectedTemplate,
  setIsTemplateSelected,
} from "@/dispatchers/templateDispatchers";
import { ConsultationButtonStatus } from "@/enum/ConsultationButtonStatus";
import {
  nhs111TemplateId,
  nhsContraceptionTemplateId,
  nhsSinusitisTemplateId,
  nhsUtiTemplateId,
  nhsSoreThroatTemplateId,
  nhsImpetigoTemplateId,
  nhsInsectBitesTemplateId,
  nhsShinglesTemplateId,
  nhsAomTemplateId,
  nhsHypertensionStage1TemplateId,
  nhs111ServiceId,
  nhsContraceptionServiceId,
  nhsHypertensionABPMFittingTemplateId,
  nhsHypertensionABPMFollowupTemplateId,
  nhsAhtWeightTemplateId,
} from "@/data/constantStrings";
import { NHSContraceptionFormData } from "@/components/nhs-templates/NHSContraceptionForm";
import { medications } from "../presriptionData";

import { isNhsService } from "@/components/nhs-templates/NHSServicesHelper";
interface ServiceCard {
  id: string;
  consultationId: string;
  patientId?: string;
  patientName: string;
  patientInitials: string;
  serviceType: string;
  date: string;
  priority?: "high" | "medium" | "low";
  notes?: string;
  service: string;
  hasLocalRecord?: boolean;
  consultationStatus?: string;
  serviceID?: string;
  referredFrom?: string;
  pathwaysDisposition?: string;
  dispositionCode?: string;
  isOverdue?: boolean;
  overdueByMs?: number;
  deadlineAt?: string;
  deadlineHours?: number | null;
  templateId?: string;
  dob?: string;
  address?: string;
  foreName?: string;
  surName?: string;
  nhsNumber?: string;
  city?: string;
  postCode?: string;
  ethnicity?: number | string;
  title?: string;
  sex?: string;
  email?: string;
  phone?: string;
}

export interface NHSEmergencyMatchedPatient {
  _id: string;
  patientID: string;
  patientName: string;
  dob: string;
  nhsNumber: string | null;
  matchScore: number;
  matchReasons: string[];
  address: string;
}

export const useNHS111Data = () => {
  const { appState, pharmacyServiceState } = useTitanApp();
  const { darkMode, currentPharmacyId, features } = appState || {};
  const {
    NHSEmergencyServiceData,
    NHSEmergencyPatientData,
    matchDialogOpen,
    matchPatient,
    isNHSEmergencyPatientLoading,
  } = pharmacyServiceState || {};
  const navigate = useNavigate();

  const handleCardClick = async (card: ServiceCard) => {
    console.log("Card clicked:", card);
    if (isNhsService(card?.templateId)) {
      if (card.hasLocalRecord === false) {
        setMatchPatient({
          name: card?.patientName,
          consultationId: card?.consultationId,
          dob: card?.dob,
          address: card?.address,
          foreName: card?.foreName,
          familyName: card?.surName,
          nhsNumber: card?.nhsNumber || "",
          city: card?.city || "",
          postCode: card?.postCode || "",
          ethnicity: card?.ethnicity,
          title: card?.title || "",
          sex: card?.sex || "",
          email: card?.email || "",
          phone: card?.phone || "",
        });
        setMatchDialogModal(true);
        await getPatientForNHSEmergencyService(card?.consultationId);
      } else {
        if (card.consultationStatus === "New") {
          const payload = {
            consultationId: card.consultationId || "",
            stageName: 2,
          };
          await updateConsultationStage(payload);
        }

        handleNavigateToConsultation(card?.patientId, card?.consultationId);
      }
    }
  };

  const handlePatientMatch = async (patientId: string) => {
    const payload = {
      consultationId: matchPatient?.consultationId || "",
      patientId: patientId,
    };

    await updateConsultationForPatientMatch(payload);
    const stagePayload = {
      consultationId: matchPatient?.consultationId || "",
      stageName: 2,
    };
    await updateConsultationStage(stagePayload);

    handleNavigateToConsultation(patientId, matchPatient?.consultationId || "");
  };

  interface NHS111EmergencySupplyFormData {
    patientConsent: "yes" | "no" | "";
    consultationDate: string;
    consultationTime: string;
    consultationMethod: "face-to-face" | "telephone" | "video" | "";
    patientAccompanied: "yes" | "no" | "";
    accompaniedRelationship: string;
    accompaniedRelationshipOther: string;
    reasonForRequest: string;
    reasonForRequestOther: string;
    ncrsRecordAccessed: "yes" | "no" | "";
    electronicPrescriptionAvailable: "yes" | "no" | "";
    outcomes: string;
    outcomesOther: string;
    referralDestination: string;
    referralDestinationOther: string;
    escalationDestination: string;
    escalationDestinationOther: string;
    onwardReferralReason: string;
    onwardReferralDate: string;
    onwardReferralTime: string;
    destinationOdsCode: string;
    adviceGiven: boolean;
    adviceProvidedTo: string;
    planAndRequestedActions: string;
    internalNotes: string;
    forwardOutOfStock: "yes" | "no" | "";
    forwardedPharmacy?: PharmacySearchResult;
    referralOrigin: string;
    referralOriginOther: string;
    referralContactDetails: string;
    referralDate: string;
    referralTime: string;
  }
  interface PharmacySearchResult {
    id: string;
    name: string;
    address: string;
    postcode: string;
    distance: number; // in miles
    telephone: string;
    openingHours: {
      monday: string;
      tuesday: string;
      wednesday: string;
      thursday: string;
      friday: string;
      saturday: string;
      sunday: string;
    };
    coordinates?: {
      lat: number;
      lng: number;
    };
  }
  const defaultNhs111Form: NHS111EmergencySupplyFormData = {
    patientConsent: "",
    consultationDate: "",
    consultationTime: "",
    consultationMethod: "",
    patientAccompanied: "",
    accompaniedRelationship: "",
    accompaniedRelationshipOther: "",
    reasonForRequest: "",
    reasonForRequestOther: "",
    ncrsRecordAccessed: "",
    electronicPrescriptionAvailable: "",
    outcomes: "",
    outcomesOther: "",
    referralDestination: "",
    referralDestinationOther: "",
    escalationDestination: "",
    escalationDestinationOther: "",
    onwardReferralReason: "",
    onwardReferralDate: "",
    onwardReferralTime: "",
    destinationOdsCode: "",
    adviceGiven: false,
    adviceProvidedTo: "",
    planAndRequestedActions: "",
    internalNotes: "",
    forwardOutOfStock: "",
    forwardedPharmacy: undefined,
    referralOrigin: "",
    referralOriginOther: "",
    referralContactDetails: "",
    referralDate: "",
    referralTime: "",
  };
  interface EmergencySupplyMedicationItem {
    id: string;
    medication: string;
    supplied: "yes" | "no" | "";
    packBrand?: string;
    quantity?: string;
    daysSupply?: string;
    doseInstructions?: string;
    notes?: string;
    notSuppliedReason?: string;
    notSuppliedReasonOther?: string;
  }

  const setNHS111ConsultationSummary = (consultationData: any) => {
    if (consultationData?.templateId === nhs111TemplateId) {
      const parsed = JSON.parse(consultationData.consultationSummary || "{}");
      const formData: Partial<NHS111EmergencySupplyFormData> =
        parsed.data ?? parsed ?? {};

      const medications: EmergencySupplyMedicationItem[] =
        parsed.emergencySupplyMedications ?? [];

      setNhs111Form({
        ...defaultNhs111Form,
        ...formData,
      });

      setNhs111EmergencySupplyMedications(medications);
      setNhs111Prescriptions([]);
    } else if (consultationData?.templateId === nhsContraceptionTemplateId) {
      const parsed = JSON.parse(consultationData.consultationSummary || "{}");
      const formData: Partial<NHSContraceptionFormData> =
        parsed.data ?? parsed ?? {};
      setNhsContraceptionForm(formData);
    } else if (consultationData?.templateId === nhsSinusitisTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsSinusitisForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsUtiTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsUtiForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsSoreThroatTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsSoreThroatForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsImpetigoTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsImpetigoForm(parsed.data ?? parsed ?? {});
    } else if (
      consultationData?.templateId === nhsHypertensionStage1TemplateId
    ) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setHypertensionInitialForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsInsectBitesTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsInsectBitesForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsShinglesTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsShinglesForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsAomTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsAomForm(parsed.data ?? parsed ?? {});
    } else if (
      consultationData?.templateId === nhsHypertensionABPMFittingTemplateId
    ) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setHypertensionABPMFittingForm(parsed.data ?? parsed ?? {});
    } else if (
      consultationData?.templateId === nhsHypertensionABPMFollowupTemplateId
    ) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setHypertensionABPMFollowupForm(parsed.data ?? parsed ?? {});
    } else if (consultationData?.templateId === nhsAhtWeightTemplateId) {
      let parsed: any = {};
      try {
        parsed = JSON.parse(consultationData.consultationSummary || "{}");
        if (typeof parsed === "string") parsed = JSON.parse(parsed);
      } catch {}
      setNhsAhtForm(parsed.data ?? parsed ?? {});
    }
  };
  const handleNavigateToConsultation = async (patientId, consultationId) => {
    var consultationData = await getPatientConsultationByConsultationId(
      consultationId,
    );
    setNHS111ConsultationSummary(consultationData);

    const patient = await getPatientByPatientId(patientId);

    await getReferralDataByConsultationId(consultationId);

    setFreeTypeMode(false);
    setIsConsultationEnabled(false);
    setSelectedPatientConsultation(consultationData);
    setConsultationId(consultationData?.consultationId);
    setPatientTranscription(consultationData?.transcription);
    setTranscript(consultationData?.transcription);
    setSelectedTemplate(consultationData?.template);
    setSignatureDetails(consultationData?.patientConsent);
    setIsFinalSummary(consultationData?.notes !== null);
    setPatientNotes(consultationData?.notes ?? "");
    setConsultationPaused(consultationData?.consultationStatus === 3);
    setShowPlayPauseBtn(consultationData?.consultationStatus === 3);
    setisHandleStartConsultation(false);
    if (
      consultationData?.template != null &&
      (consultationData?.consultationStatus === 2 ||
        consultationData?.consultationStatus === 3)
    ) {
      setIsTemplateSelected(true);
    }
    if (
      consultationData?.consultationButtonStatus ===
      ConsultationButtonStatus.Manual
    ) {
      setCurrentConsultationMediaButtonMode("manual");
    }

    setPreConsultationTemplate(consultationData?.preConsultationTemplate);

    if (consultationData?.preConsultationTemplate) {
      let preConsultTranscription = "";

      consultationData.preConsultationTemplate.templateSections?.forEach(
        (section) => {
          section?.userControls?.forEach((control) => {
            if (control.label && control.value) {
              preConsultTranscription += ` ${control.label} ${control.value} `;
            }
          });
        },
      );

      setPreConsultationTranscription(preConsultTranscription.trim());
    } else {
      setPreConsultationTranscription("");
    }
    setCurrentGlobalTabValue(features?.ifPharmacyDashboardEnabled ? 4 : 3);
    setCurrentGlobalTabText("Patients");
    setisPatientSearched(true);

    navigate(
      `/pharmacy/patientconsultation/${currentPharmacyId}/${patientId}/${consultationId}`,
    );

    setPatientInfoTab(1);

    if (consultationData?.consultationChat) {
      try {
        const parsedAiChatMessages = JSON.parse(
          consultationData.consultationChat,
        );
        setAiChatSuccessMessages();
        parsedAiChatMessages.forEach((message) => {
          setAddPatientAIChatMessage(message);
        });
      } catch (err) {
        console.error("Invalid consultationChat JSON:", err);
      }
    }
  };

  const handleAddNewPatient = () => {
    const givenName = matchPatient?.foreName || "";
    const middleName = matchPatient?.middleName || "";
    const familyName = matchPatient?.familyName || matchPatient?.surName || "";
    const postcode = matchPatient?.postCode || matchPatient?.postcode || "";
    const city = matchPatient?.city || "";

    setNewPatientFormDefaults({
      name: matchPatient?.name || "",
      nhsEmergencyConsultationId: matchPatient?.consultationId || "",
      givenName,
      middleName,
      familyName,
      dateOfBirth: matchPatient?.dob || "",
      address: matchPatient?.address || "",
      nhsNumber: matchPatient?.nhsNumber || "",
      postcode,
      ethnicity: matchPatient?.ethnicity,
      preferredAddress: {
        houseName: "",
        streetName: matchPatient?.address || "",
        locality: "",
        town: city,
        postcode,
        county: "",
      },
      pdsSearchMode: "name",

      title: matchPatient?.title || "",
      sex: matchPatient?.sex || "",
      email: matchPatient?.email || "",
      telephone: matchPatient?.phone || "",
      phone: matchPatient?.phone || "",
    });
    setMatchDialogModal(false);
    openPatientForm(true);
  };

  function formatDate(dateString) {
    const d = new Date(dateString);

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${day}.${month} ${hours}:${minutes}`;
  }

  return {
    darkMode,
    NHSEmergencyServiceData,
    NHSEmergencyPatientData,
    formatDate,
    matchDialogOpen,
    setMatchDialogModal,
    matchPatient,
    handleCardClick,
    handlePatientMatch,
    isNHSEmergencyPatientLoading,
    openPatientForm,
    handleAddNewPatient,
    setNHS111ConsultationSummary,
  };
};
